import { createSlice } from '@reduxjs/toolkit';
// eslint-disable-next-line no-restricted-imports
import {
    createStrapiMediaUrl,
    fetchCateringEventCategoriesThunk,
    fetchCateringEventFormatsThunk,
    fetchCateringFormatsThunk,
    fetchCulinaryStudioArticleCategoriesThunk,
    fetchDeliveryArticleCategoriesThunk,
    fetchDeliveryArticleTagsThunk,
    fetchOutdoorBarArticleCategoriesThunk,
    fetchSweetShopArticleCategoriesThunk,
} from 'shared/lib';
import { SweetShopArticleCategoryModel, SweetShopArticleCategoryModelSchema } from 'entities/sweet-shop-article-category';
import { CateringEventCategoryModel, CateringEventCategoryModelSchema } from 'entities/catering-event-category';
import { CateringFormatModel, CateringFormatModelSchema } from 'entities/catering-format';
import { CateringEventFormatModel, CateringEventFormatModelSchema } from 'entities/catering-event-format';
import { DeliveryArticleCategoryModel, DeliveryArticleCategoryModelSchema } from 'entities/delivery-article-category';
import { DeliveryArticleTagModel, DeliveryArticleTagModelSchema } from 'entities/delivery-article-tag';
import { CulinaryStudioArticleCategoryModel, CulinaryStudioArticleCategoryModelSchema } from 'entities/culinary-studio-article-category';
import { OutdoorBarArticleCategoryModel, OutdoorBarArticleCategoryModelSchema } from 'entities/outdoor-bar-article-category';
import { useAppSelector } from 'shared';
import BaseError from '../../../utils/base-error';

export type NavigationBlockState = {
    loaded: boolean
    errors: BaseError[]
    cateringFormats: CateringFormatModel[]
    cateringEventCategories: CateringEventCategoryModel[]
    cateringEventFormats: CateringEventFormatModel[]
    sweetShopArticleCategories: SweetShopArticleCategoryModel[]
    deliveryArticleCategories: DeliveryArticleCategoryModel[]
    deliveryArticleTags: DeliveryArticleTagModel[]
    culinaryStudioArticleCategories: CulinaryStudioArticleCategoryModel[]
    outdoorBarArticleCategories: OutdoorBarArticleCategoryModel[]
}

const initialState: NavigationBlockState = {
    loaded: false,
    errors: [],
    cateringFormats: [],
    cateringEventCategories: [],
    cateringEventFormats: [],
    sweetShopArticleCategories: [],
    deliveryArticleCategories: [],
    deliveryArticleTags: [],
    culinaryStudioArticleCategories: [],
    outdoorBarArticleCategories: [],
};

export const navigationBlockSlice = createSlice({
    name: 'navigationBlockSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        /**
         * Обработчик получения списка типов баров (коллекция OutdoorBarType)
         */
        builder.addCase(
            fetchSweetShopArticleCategoriesThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    // TODO
                } else {
                    // Валидация
                    try {
                        // eslint-disable-next-line max-len
                        state.sweetShopArticleCategories = response.data.data?.map(({
                            slug,
                            name,
                            description,
                            image,
                        }) => (
                            SweetShopArticleCategoryModelSchema.parse({
                                uid: slug,
                                name,
                                description,
                                image: image && {
                                    url: createStrapiMediaUrl(image.url),
                                },
                            })
                        ));

                        state.loaded = true;
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchCateringEventCategoriesThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    try {
                        // eslint-disable-next-line max-len
                        state.cateringEventCategories = response.data.data.map(({
                            name,
                            slug,
                        }) => (
                            CateringEventCategoryModelSchema.parse({
                                uid: slug,
                                name,
                                slug,
                            })
                        ));
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchCateringFormatsThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    try {
                        // eslint-disable-next-line max-len
                        state.cateringFormats = response.data.data?.map(({
                            name,
                            description,
                            images,
                            slug,
                        }) => (
                            CateringFormatModelSchema.parse({
                                uid: slug,
                                name,
                                description,
                                images: images?.map(({ url }) => ({
                                    url: createStrapiMediaUrl(url),
                                })),
                                slug,
                            })
                        ));
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchCateringEventFormatsThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    try {
                        // eslint-disable-next-line max-len
                        state.cateringEventFormats = response.data.data?.map(({
                            name,
                            description,
                            image,
                            category,
                            slug,
                        }) => (
                            CateringEventFormatModelSchema.parse({
                                uid: slug,
                                name,
                                description,
                                image: image && {
                                    url: createStrapiMediaUrl(image.url),
                                },
                                category: category && {
                                    uid: category.slug,
                                    name: category.name,
                                    slug: category.slug,
                                },
                                slug,
                            })
                        ));
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchDeliveryArticleCategoriesThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    try {
                        // eslint-disable-next-line max-len
                        state.deliveryArticleCategories = response.data.data.map(({
                            slug,
                            name,
                        }) => DeliveryArticleCategoryModelSchema.parse({
                            uid: slug,
                            name,
                        }));
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchDeliveryArticleTagsThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    try {
                        // eslint-disable-next-line max-len
                        state.deliveryArticleTags = response.data.data.map(({
                            slug,
                            name,
                        }) => DeliveryArticleTagModelSchema.parse({
                            uid: slug,
                            name,
                        }));
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchCulinaryStudioArticleCategoriesThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    // Валидация
                    try {
                        // eslint-disable-next-line max-len
                        state.culinaryStudioArticleCategories = response.data.data.map(({
                            slug,
                            name,
                        }) => CulinaryStudioArticleCategoryModelSchema.parse({
                            uid: slug,
                            name,
                        }));

                        state.loaded = true;
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
        builder.addCase(
            fetchOutdoorBarArticleCategoriesThunk.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    // Валидация
                    try {
                        // eslint-disable-next-line max-len
                        state.outdoorBarArticleCategories = response.data.data.map(({
                            slug,
                            name,
                        }) => OutdoorBarArticleCategoryModelSchema.parse({
                            uid: slug,
                            name,
                        }));

                        state.loaded = true;
                    } catch (e) {
                        state.errors.push(new BaseError(400, String(e)));
                    }
                }
            },
        );
    },
});

export const useNavigationBlockState = () => useAppSelector((store) => store.navigationBlock);
